<template>
   <dashboard-layout>
      <v-container fluid class="down-top-padding py-0">
         <BaseBreadcrumb
            :title="page.title"
            :icon="page.icon"
            :breadcrumbs="breadcrumbs"
         ></BaseBreadcrumb>

         <v-alert v-if="message" :type="messageType" dismissible @input="message = ''">
            <span v-html="message"></span>
         </v-alert>

         <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

         <v-row v-else>
            <v-col v-for="election in elections" :key="election.id" cols="12" lg="6">
               <v-card outlined class="h-100">
                  <v-card-title class="primary white--text">
                     <v-icon color="white" class="mr-2">mdi-ballot-outline</v-icon>
                     {{ election.title }}
                  </v-card-title>
                  <v-card-text class="pt-5">
                     <div v-if="election.description" class="mb-4" v-html="election.description"></div>
                     <v-alert type="info" text dense class="mb-4">
                        Voter registration is not required. Eligibility is checked automatically from your membership fee records.
                     </v-alert>
                     <v-alert v-if="!election.votingeligible" type="error" outlined dense class="mb-4">
                        {{ election.eligibilitymessage }}
                     </v-alert>
                     <v-simple-table dense>
                        <tbody>
                           <tr><th>Election period</th><td>{{ formatDate(election.datestart) }} to {{ formatDate(election.dateend) }}</td></tr>
                           <tr><th>Voting period</th><td>{{ formatDate(election.datevotestart) }} to {{ formatDate(election.datevoteend) }}</td></tr>
                           <tr><th>Status</th><td>{{ voterStatus(election) }}</td></tr>
                        </tbody>
                     </v-simple-table>
                  </v-card-text>
                  <v-card-actions class="px-4 pb-4 flex-wrap">
                     <v-btn
                        v-if="election.canvotenow"
                        color="primary"
                        :loading="busyId === election.id"
                        @click="openBallot(election)"
                     >View Ballot</v-btn>
                     <v-btn
                        v-else-if="!election.votingeligible"
                        color="error"
                        outlined
                        href="/member/payments"
                     >Settle Outstanding Fees</v-btn>
                     <v-chip v-else color="blue-grey" dark small>{{ election.eligibilitymessage }}</v-chip>
                     <v-btn
                        v-if="canViewResults(election)"
                        color="success"
                        outlined
                        class="ml-2"
                        :loading="resultLoading && selectedElection && selectedElection.id === election.id"
                        @click="openResults(election)"
                     >
                        <v-icon left small>mdi-trophy-outline</v-icon>
                        View Results
                     </v-btn>
                  </v-card-actions>
               </v-card>
            </v-col>

            <v-col v-if="elections.length === 0" cols="12">
               <v-alert type="info" outlined>
                  There are currently no active elections available for your membership type and zone.
               </v-alert>
            </v-col>
         </v-row>

         <v-dialog v-model="resultDialog" max-width="1000" scrollable>
            <v-card>
               <v-toolbar flat color="success" dark>
                  <v-toolbar-title>
                     <v-icon left>mdi-trophy-outline</v-icon>
                     {{ selectedElection ? selectedElection.title : 'Election Results' }}
                  </v-toolbar-title>
                  <v-spacer></v-spacer>
                  <v-btn icon @click="resultDialog = false"><v-icon>mdi-close</v-icon></v-btn>
               </v-toolbar>
               <v-card-text class="pa-5">
                  <v-progress-linear v-if="resultLoading" indeterminate color="success"></v-progress-linear>
                  <v-alert v-else-if="resultItems.length === 0" type="info" outlined>
                     No result data is available yet.
                  </v-alert>
                  <v-row v-else>
                     <v-col v-for="group in groupedResults" :key="group.category" cols="12">
                        <v-card outlined>
                           <v-card-title class="grey lighten-4">
                              {{ group.category }}
                              <v-spacer></v-spacer>
                              <v-chip color="success" small dark>{{ group.totalVotes }} votes</v-chip>
                           </v-card-title>
                           <v-list two-line>
                              <v-list-item v-for="candidate in group.candidates" :key="candidate.id">
                                 <v-list-item-avatar size="56">
                                    <v-img v-if="candidatePhoto(candidate)" :src="candidatePhoto(candidate)"></v-img>
                                    <v-icon v-else size="42" color="grey lighten-1">mdi-account-circle</v-icon>
                                 </v-list-item-avatar>
                                 <v-list-item-content>
                                    <v-list-item-title>
                                       <strong>{{ candidate.cndname }}</strong>
                                       <v-chip v-if="candidate.isWinner" x-small color="amber darken-2" dark class="ml-2">Winner</v-chip>
                                    </v-list-item-title>
                                    <v-list-item-subtitle>{{ candidate.cndmemberid || 'No Member ID' }}</v-list-item-subtitle>
                                 </v-list-item-content>
                                 <v-list-item-action class="text-right">
                                    <div class="headline font-weight-bold">{{ candidate.totalvotes || 0 }}</div>
                                    <div class="caption grey--text">votes</div>
                                 </v-list-item-action>
                              </v-list-item>
                           </v-list>
                        </v-card>
                     </v-col>
                  </v-row>
               </v-card-text>
               <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn text @click="resultDialog = false">Close</v-btn>
               </v-card-actions>
            </v-card>
         </v-dialog>
      </v-container>
   </dashboard-layout>
</template>

<script>
import BaseBreadcrumb from '@/components/BaseBreadcrumb.vue'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
const axios = require('axios')

export default {
   name: 'MOMElections',
   components: { DashboardLayout, BaseBreadcrumb },
   props: ['user'],
   data() {
      return {
         page: { title: 'Elections', icon: 'mdi-ballot-outline' },
         breadcrumbs: [
            { text: 'Dashboard', disabled: false, to: '/dashboard' },
            { text: 'Elections', disabled: true },
         ],
         elections: [],
         loading: true,
         busyId: null,
         message: '',
         messageType: 'info',
         resultDialog: false,
         resultLoading: false,
         resultItems: [],
         selectedElection: null,
      }
   },
   computed: {
      groupedResults() {
         const groups = {}
         this.resultItems.forEach(item => {
            const category = item.Category || 'Election Position'
            if (!groups[category]) groups[category] = []
            groups[category].push({
               ...item,
               totalvotes: parseInt(item.totalvotes || 0),
            })
         })

         return Object.keys(groups).map(category => {
            const candidates = groups[category].sort((a, b) => b.totalvotes - a.totalvotes || String(a.cndname).localeCompare(String(b.cndname)))
            const winningVotes = candidates.length ? candidates[0].totalvotes : 0
            return {
               category,
               totalVotes: candidates.reduce((sum, item) => sum + item.totalvotes, 0),
               candidates: candidates.map(item => ({
                  ...item,
                  isWinner: item.totalvotes === winningVotes && winningVotes > 0,
               })),
            }
         })
      },
   },
   mounted() {
      this.loadElections()
   },
   methods: {
      loadElections() {
         this.loading = true
         axios.get('/member/agm/elections')
            .then(({ data }) => {
               this.elections = data.status === 'OK' && Array.isArray(data.data) ? data.data : []
               if (data.status !== 'OK' && data.error && data.error !== 'No records') {
                  this.showMessage(data.error, 'error')
               }
            })
            .catch(() => this.showMessage('Unable to load elections. Please try again.', 'error'))
            .finally(() => { this.loading = false })
      },
      openBallot(election) {
         this.busyId = election.id
         axios.post('/member/agm/election/voter/prepare', {
            electid: election.id,
         }).then(({ data }) => {
            if (data.status === 'OK') {
               window.location.href = '/member/elections/' + election.id + '/ballot'
               return
            }
            this.showMessage(data.error || 'Unable to prepare your ballot.', 'error')
         }).catch(() => this.showMessage('Unable to prepare your ballot. Please try again.', 'error'))
           .finally(() => { this.busyId = null })
      },
      canViewResults(election) {
         if (!election) return false
         const visibility = election.result_visibility || 'after_end'
         if (visibility === 'live') return true
         if (visibility === 'hidden') return false
         if (visibility === 'after_vote') return !!election.hasvoted
         if (visibility === 'after_end') {
            const voteEnd = new Date(election.datevoteend)
            return !Number.isNaN(voteEnd.getTime()) && voteEnd < new Date()
         }
         return false
      },
      openResults(election) {
         this.selectedElection = election
         this.resultDialog = true
         this.resultLoading = true
         this.resultItems = []
         axios.get('/member/agm/electioncandidates/' + election.id)
            .then(({ data }) => {
               if (data.status === 'OK' && Array.isArray(data.data)) {
                  this.resultItems = data.data
                  return
               }
               this.showMessage(data.error || 'Unable to load election results.', 'error')
            })
            .catch(() => this.showMessage('Unable to load election results.', 'error'))
            .finally(() => { this.resultLoading = false })
      },
      voterStatus(election) {
         if (!election.votingeligible) {
            const years = Array.isArray(election.outstandingyears) ? election.outstandingyears : []
            return years.length ? 'Not eligible - outstanding fees: ' + years.join(', ') : 'Not eligible'
         }
         if (election.hasvoted) return 'Eligible - vote recorded'
         if (election.canvotenow) return 'Eligible to vote'
         return election.eligibilitymessage || 'Eligible'
      },
      formatDate(value) {
         if (!value) return 'Not specified'
         const date = new Date(value)
         return Number.isNaN(date.getTime()) ? value : date.toLocaleString('en-MY')
      },
      showMessage(message, type) {
         this.message = message || 'An unexpected error occurred.'
         this.messageType = type
      },
      candidatePhoto(candidate) {
         const value = candidate.photo_url || candidate.cndphoto || ''
         if (!value) return ''
         if (/^(https?:)?\/\//.test(value) || value.charAt(0) === '/') return value
         return '/photos/election/' + (candidate.electid || (this.selectedElection && this.selectedElection.id)) + '/' + value
      },
   },
}
</script>
